// apps/landing-page/src/components/payment/useOrderStatusPoll.ts
'use client';
import { useEffect, useRef, useState } from 'react';
import { paymentApi } from '@/lib/api/payments';

type Status = 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED' | 'REFUNDED';

const TERMINAL = new Set<Status>(['PAID', 'FAILED', 'EXPIRED', 'REFUNDED']);
const POLL_INTERVAL_MS = 4000;

/**
 * Poll-based sibling of dashboard's usePaymentStream (SSE). Landing-page has no
 * Firebase client SDK (fully migrated to the httpOnly aks_session cookie — see
 * AuthContext), so it can't mint the Firebase ID token the SSE endpoint requires
 * as a query param (EventSource can't send an Authorization header, and the
 * session cookie isn't a Firebase JWT). Polling the existing authenticated
 * GET /payment/orders/:id endpoint (already proxied via the session cookie)
 * gets the same "flip PENDING -> PAID without a refresh" outcome without any
 * backend change — see PR description for why SSE parity wasn't pursued here.
 *
 * Same return contract as usePaymentStream (a bare Status) so call sites read
 * identically across both apps.
 */
export function useOrderStatusPoll(orderId: string | null, initial: Status = 'PENDING'): Status {
  const [status, setStatus] = useState<Status>(initial);
  const pollingRef = useRef(false);

  useEffect(() => {
    setStatus(initial);
  }, [initial]);

  useEffect(() => {
    if (!orderId || TERMINAL.has(initial)) return;

    let cancelled = false;
    pollingRef.current = true;

    const tick = async () => {
      if (cancelled) return;
      try {
        const order = await paymentApi.getOrder(orderId);
        if (cancelled) return;
        setStatus(order.status as Status);
        if (TERMINAL.has(order.status as Status)) {
          pollingRef.current = false;
          return;
        }
      } catch {
        // Transient network blip — the next scheduled tick retries. No terminal
        // state is inferred from a failed fetch.
      }
      if (!cancelled && pollingRef.current) {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    // Pause polling while the tab is hidden, resume (with an immediate check) on
    // return — avoids burning requests on a backgrounded checkout tab.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && pollingRef.current && !timer) {
        tick();
      } else if (document.visibilityState === 'hidden' && timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      pollingRef.current = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [orderId, initial]);

  return status;
}
